import axios from 'axios';
import { getAuth, signOut } from 'firebase/auth';
import { getApiUrl } from '../utils/ApiUtils';

const KEY = 'ledgerloop.browser-session';
let refreshPromise;
let fallbackMarker = null;
export function sessionMarker() {
    try { return localStorage.getItem(KEY); } catch { return fallbackMarker; }
}
export function markBrowserSession(event) {
    const marker = JSON.stringify({ event, nonce: `${Date.now()}:${Math.random()}` });
    fallbackMarker = marker;
    try { localStorage.setItem(KEY, marker); } catch { /* Storage may be disabled. */ }
    return marker;
}
export function browserSignedOut() {
    try { return JSON.parse(sessionMarker())?.event === 'signed_out'; } catch { return false; }
}
export function withBrowserSessionLock(callback) {
    // Web Locks serialize cookie mutations across tabs where supported. The
    // server's bounded rotation grace also covers browsers without Web Locks.
    return typeof navigator !== 'undefined' && navigator.locks?.request
        ? navigator.locks.request('ledgerloop-browser-session', callback)
        : callback();
}
export function sessionChangedError() {
    return Object.assign(new Error('Browser session changed'), { code: 'SESSION_CHANGED' });
}
export function refreshBrowserSession() {
    if (!refreshPromise) {
        const marker = sessionMarker();
        refreshPromise = Promise.resolve().then(() => withBrowserSessionLock(async () => {
            if (browserSignedOut() || marker !== sessionMarker()) throw sessionChangedError();
            let response;
            try { response = await axios.post(getApiUrl('auth/refresh'), {}, { withCredentials: true, timeout: 15000 }); }
            catch (error) {
                if (marker !== sessionMarker()) throw sessionChangedError();
                throw error;
            }
            if (marker !== sessionMarker()) throw sessionChangedError();
            return response.data.accessToken;
        })).finally(() => { refreshPromise = null; });
    }
    return refreshPromise;
}
export async function clearBrowserIdentity({ notify = true, firebase = true } = {}) {
    if (notify) markBrowserSession('signed_out');
    for (const key of ['selectedCompany', 'userDetails']) {
        try { localStorage.removeItem(key); } catch { /* Storage may be disabled. */ }
    }
    if (firebase) await signOut(getAuth());
}
export function onBrowserSessionChange(callback) {
    const listener = event => { if (event.key === KEY) callback(); };
    window.addEventListener('storage', listener);
    return () => window.removeEventListener('storage', listener);
}
